import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  ClockClockwise, SignIn, SignOut, CaretLeft, CaretRight, MapPin, Camera, Clock, ShieldSlash, Warning, TrendUp, LockOpen, FilePdf, FileXls, Export
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// ─── Cierre de Mes Section Component ───
const CierreMesSection = ({ axiosInstance, isAdmin, branches, filters, setFilters, userBranchId }) => {
  const [cierreMonth, setCierreMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
  const [cierreBranch, setCierreBranch] = useState(!isAdmin && userBranchId ? userBranchId : '');
  const [dcRows, setDcRows] = useState([]);
  const [dcLoading, setDcLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localEdits, setLocalEdits] = useState({});

  const loadDobleCheck = useCallback(async () => {
    if (!cierreBranch) return;
    setDcLoading(true);
    try {
      const [y, m] = cierreMonth.split('-');
      const res = await axiosInstance.get(`/api/asistencia/doble-check?year=${y}&month=${parseInt(m)}&branch_id=${cierreBranch}`);
      setDcRows(res.data.rows || []);
      const edits = {};
      (res.data.rows || []).forEach(r => {
        edits[r.worker_id] = { dias_libro: r.dias_libro ?? '', he_libro: r.he_libro ?? '' };
      });
      setLocalEdits(edits);
    } catch (e) { toast.error('Error al cargar datos'); }
    finally { setDcLoading(false); }
  }, [axiosInstance, cierreMonth, cierreBranch]);

  useEffect(() => { loadDobleCheck(); }, [loadDobleCheck]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(localEdits).map(([wid, vals]) => ({
        worker_id: wid,
        dias_libro: vals.dias_libro !== '' ? Number(vals.dias_libro) : null,
        he_libro: vals.he_libro !== '' ? Number(vals.he_libro) : null,
      }));
      await axiosInstance.post('/api/asistencia/doble-check', {
        branch_id: cierreBranch,
        month: cierreMonth,
        entries,
      });
      toast.success('Datos de libro guardados');
      loadDobleCheck();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleExportFinal = async () => {
    try {
      const [y, m] = cierreMonth.split('-');
      const params = new URLSearchParams({ year: y, month: parseInt(m) });
      if (cierreBranch) params.append('branch_id', cierreBranch);
      const res = await axiosInstance.get(`/api/asistencia/exportar-cierre-final?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cierre_final_${y}_${m}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Cierre final exportado');
    } catch (e) { toast.error('Error al exportar'); }
  };

  const handleExportSimple = async () => {
    try {
      const [y, m] = cierreMonth.split('-');
      const params = new URLSearchParams({ year: y, month: parseInt(m) });
      if (cierreBranch) params.append('branch_id', cierreBranch);
      const res = await axiosInstance.get(`/api/asistencia/exportar-cierre-mes?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cierre_mes_${y}_${m}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Cierre de mes exportado');
    } catch (e) { toast.error('Error al exportar'); }
  };

  const updateEdit = (wid, field, value) => {
    setLocalEdits(prev => ({ ...prev, [wid]: { ...prev[wid], [field]: value } }));
  };

  const hasDiscrepancies = dcRows.some(r => {
    const ed = localEdits[r.worker_id];
    if (!ed) return false;
    const dl = ed.dias_libro !== '' ? Number(ed.dias_libro) : null;
    const hl = ed.he_libro !== '' ? Number(ed.he_libro) : null;
    return (dl !== null && dl !== r.dias_sistema) || (hl !== null && hl !== r.he_sistema);
  });

  return (
    <div className="space-y-4" data-testid="cierre-mes-section">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end bg-white border border-zinc-200 p-4">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Mes</label>
          <input type="month" value={cierreMonth} onChange={e => setCierreMonth(e.target.value)} className="form-input text-sm font-mono" data-testid="cierre-mes-month" />
        </div>
        {isAdmin && (
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Sucursal</label>
            <select value={cierreBranch} onChange={e => setCierreBranch(e.target.value)} className="form-select text-sm w-44" data-testid="cierre-mes-branch">
              <option value="">Todos los locales</option>
              {branches.filter(b => b.is_active !== false).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={handleExportSimple} className="px-3 py-2 text-xs font-medium border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 transition-colors flex items-center gap-1.5" data-testid="export-cierre-simple">
            <FileXls size={14} /> Exportar Sin Check
          </button>
          <button onClick={handleExportFinal} className="px-3 py-2 text-xs font-semibold bg-zinc-900 text-white hover:bg-zinc-800 transition-colors flex items-center gap-1.5" data-testid="export-cierre-final">
            <FileXls size={14} /> Exportar Cierre Final
          </button>
        </div>
      </div>

      {/* Doble Check Table */}
      <div className="bg-white border border-zinc-200" data-testid="doble-check-section">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
          <Warning size={18} className="text-blue-500" />
          <h3 className="text-sm font-bold text-zinc-900">Doble Check — Sistema vs Libro</h3>
          {hasDiscrepancies && <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 border border-amber-200 ml-2">Hay diferencias</span>}
          <button onClick={handleSave} disabled={saving || !cierreBranch} className="ml-auto px-3 py-1.5 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50" data-testid="save-doble-check">
            {saving ? 'Guardando...' : 'Guardar Libro'}
          </button>
        </div>

        {!cierreBranch ? (
          <div className="px-4 py-8 text-center text-sm text-zinc-400">
            Selecciona un local para ver el Doble Check. La exportacion con &ldquo;Todos los locales&rdquo; incluira todas las pestanas.
          </div>
        ) : dcLoading ? (
          <div className="flex items-center justify-center h-32"><div className="spinner"></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-blue-600">Dias (Sistema)</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-amber-600">Dias (Libro)</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Dif.</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-blue-600">HE (Sistema)</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-amber-600">HE (Libro)</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Dif.</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Estado</th>
                </tr>
              </thead>
              <tbody>
                {dcRows.map(r => {
                  const ed = localEdits[r.worker_id] || { dias_libro: '', he_libro: '' };
                  const dl = ed.dias_libro !== '' ? Number(ed.dias_libro) : null;
                  const hl = ed.he_libro !== '' ? Number(ed.he_libro) : null;
                  const diffD = dl !== null ? r.dias_sistema - dl : null;
                  const diffH = hl !== null ? Math.round((r.he_sistema - hl) * 10) / 10 : null;
                  const ok = dl !== null && hl !== null && diffD === 0 && diffH === 0;
                  const warn = (diffD !== null && diffD !== 0) || (diffH !== null && diffH !== 0);

                  return (
                    <tr key={r.worker_id} className={`border-b border-zinc-100 ${warn ? 'bg-red-50/30' : ok ? 'bg-emerald-50/30' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="text-xs font-medium text-zinc-900">{r.name}</div>
                        <div className="text-[10px] text-zinc-400">{r.code}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600">{r.cargo}</td>
                      <td className="px-3 py-2 text-center font-mono text-xs font-bold text-blue-700">{r.dias_sistema}</td>
                      <td className="px-3 py-1 text-center">
                        <input type="number" min="0" max="31" value={ed.dias_libro}
                          onChange={e => updateEdit(r.worker_id, 'dias_libro', e.target.value)}
                          className="w-14 text-center text-xs font-mono border border-amber-200 bg-amber-50/50 py-1 focus:outline-none focus:border-amber-400"
                          data-testid={`dc-dias-${r.worker_id}`} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {diffD !== null ? (
                          <span className={`font-mono text-[10px] font-bold ${diffD === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {diffD === 0 ? '0' : (diffD > 0 ? `+${diffD}` : diffD)}
                          </span>
                        ) : <span className="text-zinc-300 text-[10px]">-</span>}
                      </td>
                      <td className="px-3 py-2 text-center font-mono text-xs font-bold text-blue-700">{r.he_sistema}</td>
                      <td className="px-3 py-1 text-center">
                        <input type="number" min="0" step="0.5" value={ed.he_libro}
                          onChange={e => updateEdit(r.worker_id, 'he_libro', e.target.value)}
                          className="w-14 text-center text-xs font-mono border border-amber-200 bg-amber-50/50 py-1 focus:outline-none focus:border-amber-400"
                          data-testid={`dc-he-${r.worker_id}`} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {diffH !== null ? (
                          <span className={`font-mono text-[10px] font-bold ${diffH === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {diffH === 0 ? '0' : (diffH > 0 ? `+${diffH}` : diffH)}
                          </span>
                        ) : <span className="text-zinc-300 text-[10px]">-</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {ok ? <span className="text-emerald-600 text-xs font-bold">OK</span>
                          : warn ? <span className="text-red-600 text-xs font-bold">DIF</span>
                          : <span className="text-zinc-300 text-[10px]">-</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const AttendanceAdminPage = () => {
  const { axiosInstance, isAdmin, user, canManageCierreMes } = useAuth();
  const [activeTab, setActiveTab] = useState('planilla');
  const [jornadas, setJornadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [photoModal, setPhotoModal] = useState(null);
  const [resetPinWorker, setResetPinWorker] = useState('');
  const [resettingPin, setResettingPin] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [turnoVsReal, setTurnoVsReal] = useState(null);
  const [turnoVsRealLoading, setTurnoVsRealLoading] = useState(false);
  const [autoCierres, setAutoCierres] = useState(null);
  const [autoCierresLoading, setAutoCierresLoading] = useState(false);
  const [filters, setFilters] = useState({
    branch_id: '',
    worker_id: '',
    fecha_desde: new Date().toISOString().split('T')[0],
    fecha_hasta: new Date().toISOString().split('T')[0],
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const loadMeta = useCallback(async () => {
    try {
      const [bRes, wRes, cRes] = await Promise.all([
        axiosInstance.get('/api/branches'),
        axiosInstance.get('/api/workers'),
        axiosInstance.get('/api/cargos'),
      ]);
      setBranches(bRes.data);
      setWorkers(wRes.data);
      setCargos(cRes.data.filter(c => c.is_active));
      if (!isAdmin && user?.branch_id) {
        setFilters(f => ({ ...f, branch_id: user.branch_id }));
      }
    } catch (e) { /* silenced */ }
  }, [axiosInstance, isAdmin, user]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const loadJornadas = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 50 };
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.worker_id) params.worker_id = filters.worker_id;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      const res = await axiosInstance.get('/api/asistencia/jornadas', { params });
      setJornadas(res.data.jornadas || []);
      setPagination(p => ({ ...p, pages: res.data.pages, total: res.data.total }));
    } catch (e) { /* silenced */ }
    finally { setLoading(false); }
  }, [axiosInstance, filters, pagination.page]);

  useEffect(() => { loadJornadas(); }, [loadJornadas]);

  const loadAnalysis = useCallback(async () => {
    try {
      setAnalysisLoading(true);
      const params = {};
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.cargo_id) params.cargo_id = filters.cargo_id;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      const res = await axiosInstance.get('/api/asistencia/analisis', { params });
      setAnalysis(res.data);
    } catch (e) { /* silenced */ }
    finally { setAnalysisLoading(false); }
  }, [axiosInstance, filters.branch_id, filters.cargo_id, filters.fecha_desde, filters.fecha_hasta]);

  useEffect(() => { if (activeTab === 'analisis') loadAnalysis(); }, [activeTab, loadAnalysis]);

  const loadTurnoVsReal = useCallback(async () => {
    try {
      setTurnoVsRealLoading(true);
      const params = {};
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.cargo_id) params.cargo_id = filters.cargo_id;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      const res = await axiosInstance.get('/api/asistencia/turno-vs-real', { params });
      setTurnoVsReal(res.data);
    } catch (e) { /* silenced */ }
    finally { setTurnoVsRealLoading(false); }
  }, [axiosInstance, filters.branch_id, filters.cargo_id, filters.fecha_desde, filters.fecha_hasta]);

  useEffect(() => { if (activeTab === 'turno-vs-real') loadTurnoVsReal(); }, [activeTab, loadTurnoVsReal]);

  const loadAutoCierres = useCallback(async () => {
    try {
      setAutoCierresLoading(true);
      const params = {};
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      const res = await axiosInstance.get('/api/asistencia/cierres-automaticos', { params });
      setAutoCierres(res.data);
    } catch (e) { /* silenced */ }
    finally { setAutoCierresLoading(false); }
  }, [axiosInstance, filters.branch_id, filters.fecha_desde, filters.fecha_hasta]);

  useEffect(() => { if (activeTab === 'cierres') loadAutoCierres(); }, [activeTab, loadAutoCierres]);

  const branchMap = {};
  branches.forEach(b => { branchMap[b.id] = b.name; });

  const filteredWorkers = filters.branch_id
    ? workers.filter(w => w.branch_id === filters.branch_id)
    : workers;

  const colCount = isAdmin ? 8 : 7;

  return (
    <div className="space-y-4" data-testid="attendance-admin-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900">Asistencia</h1>
        <p className="text-sm text-zinc-500 mt-1">Planilla de jornadas por QR</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200" data-testid="attendance-tabs">
        <button
          onClick={() => setActiveTab('planilla')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'planilla' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
          data-testid="tab-planilla"
        >
          <ClockClockwise size={14} className="inline mr-1.5" />
          Planilla
        </button>
        <button
          onClick={() => setActiveTab('analisis')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'analisis' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
          data-testid="tab-analisis"
        >
          <TrendUp size={14} className="inline mr-1.5" />
          Retrasos y Pendientes
        </button>
        <button
          onClick={() => setActiveTab('turno-vs-real')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'turno-vs-real' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
          data-testid="tab-turno-vs-real"
        >
          <Clock size={14} className="inline mr-1.5" />
          Turno vs Real
        </button>
        <button
          onClick={() => setActiveTab('cierres')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'cierres' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
          data-testid="tab-cierres"
        >
          <LockOpen size={14} className="inline mr-1.5" />
          Cierres Automaticos
        </button>
        {canManageCierreMes && (
        <button
          onClick={() => setActiveTab('cierre-mes')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === 'cierre-mes' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
          data-testid="tab-cierre-mes"
        >
          <Export size={14} className="inline mr-1.5" />
          Cierre de Mes
        </button>
        )}
      </div>

      {activeTab === 'planilla' && (<>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end bg-white border border-zinc-200 p-4" data-testid="attendance-filters">
        {isAdmin && (
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Sucursal</label>
            <select
              value={filters.branch_id}
              onChange={(e) => { setFilters({ ...filters, branch_id: e.target.value, worker_id: '' }); setPagination(p => ({ ...p, page: 1 })); }}
              className="form-select text-sm w-44"
              data-testid="filter-branch"
            >
              <option value="">Todas</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Trabajador</label>
          <select
            value={filters.worker_id}
            onChange={(e) => { setFilters({ ...filters, worker_id: e.target.value }); setPagination(p => ({ ...p, page: 1 })); }}
            className="form-select text-sm w-44"
            data-testid="filter-worker"
          >
            <option value="">Todos</option>
            {filteredWorkers.map(w => (
              <option key={w.id} value={w.id}>{w.first_name} {w.last_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Desde</label>
          <input
            type="date"
            value={filters.fecha_desde}
            onChange={(e) => { setFilters({ ...filters, fecha_desde: e.target.value }); setPagination(p => ({ ...p, page: 1 })); }}
            className="form-input text-sm font-mono"
            data-testid="filter-desde"
          />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Hasta</label>
          <input
            type="date"
            value={filters.fecha_hasta}
            onChange={(e) => { setFilters({ ...filters, fecha_hasta: e.target.value }); setPagination(p => ({ ...p, page: 1 })); }}
            className="form-input text-sm font-mono"
            data-testid="filter-hasta"
          />
        </div>
      </div>

      {/* PIN Reset */}
      <div className="flex flex-wrap gap-3 items-end bg-white border border-zinc-200 p-4" data-testid="pin-reset-section">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Resetear PIN de trabajador</label>
          <select
            value={resetPinWorker}
            onChange={(e) => setResetPinWorker(e.target.value)}
            className="form-select text-sm w-full"
            data-testid="reset-pin-select"
          >
            <option value="">-- Seleccionar trabajador --</option>
            {filteredWorkers.map(w => (
              <option key={w.id} value={w.id}>{w.first_name} {w.last_name} ({w.code})</option>
            ))}
          </select>
        </div>
        <button
          disabled={!resetPinWorker || resettingPin}
          onClick={async () => {
            if (!resetPinWorker) return;
            setResettingPin(true);
            try {
              const res = await axiosInstance.post(`/api/asistencia/reset-pin/${resetPinWorker}`);
              toast.success(res.data.message);
              setResetPinWorker('');
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Error al resetear PIN');
            } finally {
              setResettingPin(false);
            }
          }}
          className="px-4 py-2 text-sm font-medium border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          data-testid="reset-pin-btn"
        >
          <ShieldSlash size={16} />
          {resettingPin ? 'Reseteando...' : 'Resetear PIN'}
        </button>
      </div>

      {/* Summary + Export */}
      <div className="flex items-center gap-4 text-sm text-zinc-500">
        <ClockClockwise size={16} />
        <span><strong className="text-zinc-700">{pagination.total}</strong> jornadas encontradas</span>
        <button
          onClick={async () => {
            try {
              const [y, m] = filters.fecha_desde ? filters.fecha_desde.split('-') : [new Date().getFullYear(), String(new Date().getMonth() + 1).padStart(2, '0')];
              const params = new URLSearchParams({ year: y, month: parseInt(m) });
              if (filters.branch_id) params.append('branch_id', filters.branch_id);
              const res = await axiosInstance.get(`/api/asistencia/exportar-excel?${params.toString()}`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `asistencia_${y}_${m}.xlsx`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              toast.success('Excel exportado');
            } catch (e) { toast.error('Error al exportar'); }
          }}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
          data-testid="export-attendance-excel"
        >
          <FileXls size={14} /> Exportar Excel
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="attendance-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Fecha</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                {isAdmin && <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Sucursal</th>}
                <th className="text-center px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">
                  <span className="inline-flex items-center gap-1"><SignIn size={10} /> Entrada</span>
                </th>
                <th className="text-center px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">
                  <span className="inline-flex items-center gap-1"><SignOut size={10} /> Salida</span>
                </th>
                <th className="text-center px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">
                  <span className="inline-flex items-center gap-1"><Clock size={10} /> Horas</span>
                </th>
                <th className="text-center px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Dist.</th>
                <th className="text-center px-3 py-2.5 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Fotos</th>
              </tr>
            </thead>
            <tbody>
              {jornadas.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="text-center py-8 text-zinc-400 text-sm">Sin registros para los filtros seleccionados</td>
                </tr>
              ) : jornadas.map((j, i) => (
                <tr key={`${j.worker_id}-${j.fecha}-${i}`} className={`border-b border-zinc-100 hover:bg-zinc-50/50 ${j.jornada_abierta ? 'bg-amber-50/30' : ''}`}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-700">{j.fecha}</td>
                  <td className="px-3 py-2">
                    <div className="text-sm font-medium text-zinc-900">{j.worker_name}</div>
                    <div className="text-[10px] text-zinc-400">{j.worker_code}</div>
                  </td>
                  {isAdmin && <td className="px-3 py-2 text-xs text-zinc-600">{branchMap[j.branch_id] || j.branch_id}</td>}
                  {/* Entrada */}
                  <td className="px-3 py-2 text-center">
                    {j.entrada_hora ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                        {j.entrada_hora.slice(0, 5)}
                      </span>
                    ) : <span className="text-zinc-300 text-[10px]">-</span>}
                  </td>
                  {/* Salida */}
                  <td className="px-3 py-2 text-center">
                    {j.salida_hora ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 font-mono">
                        {j.salida_hora.slice(0, 5)}
                      </span>
                    ) : j.jornada_abierta ? (
                      <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 border border-amber-200">Pendiente</span>
                    ) : <span className="text-zinc-300 text-[10px]">-</span>}
                  </td>
                  {/* Horas */}
                  <td className="px-3 py-2 text-center">
                    {j.horas_trabajadas != null ? (
                      <span className="font-mono text-xs font-bold text-zinc-800">{j.horas_trabajadas}</span>
                    ) : j.jornada_abierta ? (
                      <span className="text-[10px] text-amber-500">...</span>
                    ) : <span className="text-zinc-300 text-[10px]">-</span>}
                  </td>
                  {/* Distance */}
                  <td className="px-3 py-2 text-center text-[10px]">
                    {(j.entrada_distance != null || j.salida_distance != null) ? (
                      <div className="flex flex-col items-center gap-0.5">
                        {j.entrada_distance != null && (
                          <span className="inline-flex items-center gap-0.5 text-emerald-600"><MapPin size={8} />{j.entrada_distance}m</span>
                        )}
                        {j.salida_distance != null && (
                          <span className="inline-flex items-center gap-0.5 text-rose-600"><MapPin size={8} />{j.salida_distance}m</span>
                        )}
                      </div>
                    ) : <span className="text-zinc-300">-</span>}
                  </td>
                  {/* Photos - admin only */}
                  <td className="px-3 py-2 text-center">
                    {isAdmin ? (
                    <div className="flex items-center justify-center gap-1.5">
                      {j.entrada_photo && (
                        <button
                          onClick={() => setPhotoModal({ photo_filename: j.entrada_photo, worker_name: j.worker_name, worker_code: j.worker_code, fecha: j.fecha, hora: j.entrada_hora, tipo: 'entrada', distance_meters: j.entrada_distance })}
                          className="text-emerald-600 hover:text-emerald-800" title="Foto entrada"
                          data-testid={`photo-entrada-${i}`}
                        >
                          <Camera size={14} />
                        </button>
                      )}
                      {j.salida_photo && (
                        <button
                          onClick={() => setPhotoModal({ photo_filename: j.salida_photo, worker_name: j.worker_name, worker_code: j.worker_code, fecha: j.fecha, hora: j.salida_hora, tipo: 'salida', distance_meters: j.salida_distance })}
                          className="text-rose-600 hover:text-rose-800" title="Foto salida"
                          data-testid={`photo-salida-${i}`}
                        >
                          <Camera size={14} />
                        </button>
                      )}
                      {!j.entrada_photo && !j.salida_photo && <span className="text-zinc-300 text-[10px]">-</span>}
                    </div>
                    ) : <span className="text-zinc-300 text-[10px]">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
            disabled={pagination.page <= 1}
            className="p-1.5 border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30"
            data-testid="prev-page"
          >
            <CaretLeft size={14} />
          </button>
          <span className="text-xs text-zinc-500">Pagina {pagination.page} de {pagination.pages}</span>
          <button
            onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
            disabled={pagination.page >= pagination.pages}
            className="p-1.5 border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30"
            data-testid="next-page"
          >
            <CaretRight size={14} />
          </button>
        </div>
      )}
      </>)}

      {/* Analysis Tab */}
      {activeTab === 'analisis' && (
        <div className="space-y-4" data-testid="analysis-section">
          {/* Analysis Filters */}
          <div className="flex flex-wrap gap-3 items-end bg-white border border-zinc-200 p-4" data-testid="analysis-filters">
            {isAdmin && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Sucursal</label>
                <select
                  value={filters.branch_id}
                  onChange={(e) => { setFilters({ ...filters, branch_id: e.target.value }); }}
                  className="form-select text-sm w-44"
                  data-testid="analysis-filter-branch"
                >
                  <option value="">Todas</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Cargo</label>
              <select
                value={filters.cargo_id || ''}
                onChange={(e) => setFilters({ ...filters, cargo_id: e.target.value })}
                className="form-select text-sm w-40"
                data-testid="analysis-filter-cargo"
              >
                <option value="">Todos</option>
                {(cargos || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Desde</label>
              <input type="date" value={filters.fecha_desde}
                onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                className="form-input text-sm font-mono" data-testid="analysis-filter-desde" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Hasta</label>
              <input type="date" value={filters.fecha_hasta}
                onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                className="form-input text-sm font-mono" data-testid="analysis-filter-hasta" />
            </div>
          </div>

          {analysisLoading ? (
            <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
          ) : analysis ? (
            <>
              {/* Retrasos ranking */}
              <div className="bg-white border border-zinc-200" data-testid="retrasos-table">
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                  <Warning size={18} className="text-amber-500" />
                  <h3 className="text-sm font-bold text-zinc-900">Ranking de Retrasos</h3>
                  <span className="text-[10px] text-zinc-400 ml-auto">{analysis.fecha_desde} al {analysis.fecha_hasta}</span>
                </div>
                {analysis.retrasos.length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-6">Sin retrasos en el periodo</p>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-100">
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">#</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Dias</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Turno</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-emerald-600">Entr. tarde</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-emerald-600">Prom. entrada</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-rose-600">Sal. tarde</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-rose-600">Prom. salida</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.retrasos.map((r, i) => (
                        <tr key={r.worker_id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                          <td className="px-3 py-2 text-xs font-bold text-zinc-400">{i + 1}</td>
                          <td className="px-3 py-2">
                            <div className="text-sm font-medium text-zinc-900">{r.worker_name}</div>
                            <div className="text-[10px] text-zinc-400">{r.worker_code}</div>
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-600">{r.cargo}</td>
                          <td className="px-3 py-2 text-center text-xs text-zinc-500">{r.total_days}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="font-mono text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200">{r.turno_esperado}</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.entry_delay_count > 0 ? (
                              <span className="font-mono text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 border border-amber-200">{r.entry_delay_count}x</span>
                            ) : <span className="text-zinc-300 text-[10px]">-</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.avg_entry_delay > 0 ? (
                              <span className="font-mono text-xs font-bold text-zinc-800">{r.avg_entry_display}</span>
                            ) : <span className="text-zinc-300 text-[10px]">-</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.exit_delay_count > 0 ? (
                              <span className="font-mono text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 border border-rose-200">{r.exit_delay_count}x</span>
                            ) : <span className="text-zinc-300 text-[10px]">-</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.avg_exit_delay > 0 ? (
                              <span className="font-mono text-xs font-bold text-zinc-800">{r.avg_exit_display}</span>
                            ) : <span className="text-zinc-300 text-[10px]">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>

              {/* Salidas pendientes */}
              <div className="bg-white border border-zinc-200" data-testid="pendientes-table">
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                  <SignOut size={18} className="text-rose-500" />
                  <h3 className="text-sm font-bold text-zinc-900">Salidas Pendientes</h3>
                  <span className="text-[10px] text-zinc-400 ml-auto">Jornadas abiertas sin salida</span>
                </div>
                {analysis.salidas_pendientes.length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-6">Sin salidas pendientes</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-100">
                        <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">#</th>
                        <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                        <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
                        <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Entrada</th>
                        <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Tiempo sin salida</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.salidas_pendientes.map((s, i) => (
                        <tr key={s.worker_id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                          <td className="px-4 py-2 text-xs font-bold text-zinc-400">{i + 1}</td>
                          <td className="px-4 py-2">
                            <div className="text-sm font-medium text-zinc-900">{s.worker_name}</div>
                            <div className="text-[10px] text-zinc-400">{s.worker_code}</div>
                          </td>
                          <td className="px-4 py-2 text-xs text-zinc-600">{s.cargo}</td>
                          <td className="px-4 py-2 text-center">
                            <div className="text-xs font-mono text-zinc-700">{s.entrada_fecha}</div>
                            <div className="text-[10px] font-mono text-emerald-600">{s.entrada_hora?.slice(0,5)}</div>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className="font-mono text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 border border-rose-200">{s.horas_display}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-zinc-400 text-center py-8">Seleccione filtros para ver el analisis</p>
          )}
        </div>
      )}

      {/* Turno vs Real Tab */}
      {activeTab === 'turno-vs-real' && (
        <div className="space-y-4" data-testid="turno-vs-real-section">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end bg-white border border-zinc-200 p-4">
            {isAdmin && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Sucursal</label>
                <select value={filters.branch_id} onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
                  className="form-select text-sm w-44" data-testid="tvr-filter-branch">
                  <option value="">Todas</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Cargo</label>
              <select value={filters.cargo_id || ''} onChange={(e) => setFilters({ ...filters, cargo_id: e.target.value })}
                className="form-select text-sm w-40" data-testid="tvr-filter-cargo">
                <option value="">Todos</option>
                {(cargos || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Desde</label>
              <input type="date" value={filters.fecha_desde} onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                className="form-input text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Hasta</label>
              <input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                className="form-input text-sm font-mono" />
            </div>
          </div>

          {turnoVsRealLoading ? (
            <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
          ) : turnoVsReal ? (
            <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="tvr-table">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                <Clock size={18} className="text-blue-500" />
                <h3 className="text-sm font-bold text-zinc-900">Turno Asignado vs Asistencia Real</h3>
                <span className="text-[10px] text-zinc-400 ml-auto mr-3">{turnoVsReal.total} registros</span>
                <button
                  onClick={async () => {
                    try {
                      const params = new URLSearchParams();
                      if (filters.branch_id) params.append('branch_id', filters.branch_id);
                      if (filters.cargo_id) params.append('cargo_id', filters.cargo_id);
                      if (filters.fecha_desde) params.append('fecha_desde', filters.fecha_desde);
                      if (filters.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta);
                      const res = await axiosInstance.get(`/api/asistencia/turno-vs-real-pdf?${params.toString()}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `turno_vs_real_${filters.fecha_desde || 'hoy'}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('PDF exportado');
                    } catch (e) { toast.error('Error al exportar PDF'); }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                  data-testid="export-tvr-pdf"
                >
                  <FilePdf size={14} /> Exportar PDF
                </button>
              </div>
              {turnoVsReal.rows.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-6">Sin datos para el periodo seleccionado</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-100">
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Fecha</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Turno</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-emerald-600">Entrada real</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-rose-600">Salida real</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Horas</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Dif. entrada</th>
                      <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Dif. salida</th>
                    </tr>
                  </thead>
                  <tbody>
                    {turnoVsReal.rows.map((r, i) => (
                      <tr key={`${r.worker_id}-${r.fecha}-${i}`} className={`border-b border-zinc-100 hover:bg-zinc-50/50 ${r.sin_marcar ? 'bg-red-50/30' : ''}`}>
                        <td className="px-3 py-2 font-mono text-xs text-zinc-700">{r.fecha}</td>
                        <td className="px-3 py-2">
                          <div className="text-xs font-medium text-zinc-900">{r.worker_name}</div>
                          <div className="text-[10px] text-zinc-400">{r.worker_code}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-zinc-600">{r.cargo}</td>
                        <td className="px-3 py-2 text-center">
                          {r.turno_inicio ? (
                            <div>
                              <span className="font-mono text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">{r.turno_inicio}-{r.turno_fin}</span>
                              {r.turno_nombre && <div className="text-[9px] text-zinc-400 mt-0.5">{r.turno_nombre}</div>}
                            </div>
                          ) : <span className="text-zinc-300 text-[10px]">Sin turno</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.entrada_real ? (
                            <span className="font-mono text-xs font-bold text-emerald-700">{r.entrada_real}</span>
                          ) : r.sin_marcar ? (
                            <span className="text-[10px] text-red-500 font-medium">No marco</span>
                          ) : <span className="text-zinc-300 text-[10px]">-</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.salida_real ? (
                            <span className="font-mono text-xs font-bold text-rose-700">{r.salida_real}</span>
                          ) : <span className="text-zinc-300 text-[10px]">-</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.horas_trabajadas ? (
                            <span className="font-mono text-xs font-bold text-zinc-800">{r.horas_trabajadas}</span>
                          ) : <span className="text-zinc-300 text-[10px]">-</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.delay_entrada != null && r.delay_entrada > 0 ? (
                            <span className="font-mono text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 border border-amber-200">+{r.delay_entrada}min</span>
                          ) : r.delay_entrada === 0 ? (
                            <span className="text-[10px] text-emerald-600">OK</span>
                          ) : <span className="text-zinc-300 text-[10px]">-</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.delay_salida != null && r.delay_salida > 0 ? (
                            <span className="font-mono text-[10px] font-bold text-rose-700 bg-rose-50 px-1.5 py-0.5 border border-rose-200">+{r.delay_salida}min</span>
                          ) : r.delay_salida === 0 ? (
                            <span className="text-[10px] text-emerald-600">OK</span>
                          ) : <span className="text-zinc-300 text-[10px]">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Cierres Automaticos Tab */}
      {activeTab === 'cierres' && (
        <div className="space-y-4" data-testid="cierres-section">
          <div className="flex flex-wrap gap-3 items-end bg-white border border-zinc-200 p-4">
            {isAdmin && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Sucursal</label>
                <select value={filters.branch_id} onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
                  className="form-select text-sm w-44">
                  <option value="">Todas</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Desde</label>
              <input type="date" value={filters.fecha_desde} onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                className="form-input text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Hasta</label>
              <input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                className="form-input text-sm font-mono" />
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-700 flex items-center gap-2">
            <Warning size={16} className="shrink-0" />
            <span>Las jornadas sin salida registrada se cierran automaticamente a las <strong>05:00 AM</strong> del dia siguiente. Estos registros aparecen aqui.</span>
          </div>

          {autoCierresLoading ? (
            <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
          ) : autoCierres ? (
            <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="cierres-table">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                <LockOpen size={18} className="text-amber-500" />
                <h3 className="text-sm font-bold text-zinc-900">Salidas no registradas (cerradas a las 05:00)</h3>
                <span className="text-[10px] text-zinc-400 ml-auto">{autoCierres.total} registros</span>
              </div>
              {autoCierres.records.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-6">Sin cierres automaticos en el periodo</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-100">
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                      {isAdmin && <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Sucursal</th>}
                      <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-emerald-600">Entrada</th>
                      <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Dia sin salida</th>
                      <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-amber-600">Cierre automatico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoCierres.records.map((r, i) => (
                      <tr key={`${r.worker_id}-${r.entrada_fecha}-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                        <td className="px-4 py-2">
                          <div className="text-sm font-medium text-zinc-900">{r.worker_name}</div>
                          <div className="text-[10px] text-zinc-400">{r.worker_code}</div>
                        </td>
                        {isAdmin && <td className="px-4 py-2 text-xs text-zinc-600">{branchMap[r.branch_id] || r.branch_id}</td>}
                        <td className="px-4 py-2 text-center">
                          <div className="text-xs font-mono text-zinc-700">{r.entrada_fecha}</div>
                          <div className="text-[10px] font-mono text-emerald-600 font-bold">{r.entrada_hora?.slice(0,5)}</div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className="text-xs text-red-600 font-medium">{r.entrada_fecha}</span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <div className="text-xs font-mono text-zinc-700">{r.cierre_fecha}</div>
                          <span className="text-[10px] font-mono font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 border border-amber-200">05:00</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Cierre de Mes Tab */}
      {activeTab === 'cierre-mes' && canManageCierreMes && (
        <CierreMesSection
          axiosInstance={axiosInstance}
          isAdmin={isAdmin}
          branches={branches}
          filters={filters}
          setFilters={setFilters}
          userBranchId={user?.branch_id}
        />
      )}

      {/* Photo Modal */}
      {photoModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPhotoModal(null)}>
          <div className="bg-white border border-zinc-200 p-4 max-w-sm w-full space-y-3" onClick={(e) => e.stopPropagation()} data-testid="photo-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-zinc-900">Foto de marcacion</h3>
              <button onClick={() => setPhotoModal(null)} className="text-zinc-400 hover:text-zinc-700 text-sm">X</button>
            </div>
            <div className="text-xs text-zinc-500">
              <p>{photoModal.worker_name} · {photoModal.worker_code}</p>
              <p>{photoModal.fecha} {photoModal.hora?.slice(0,5)} · {photoModal.tipo === 'entrada' ? 'Entrada' : 'Salida'}</p>
              {photoModal.distance_meters != null && <p className="flex items-center gap-1 mt-0.5"><MapPin size={10} /> {photoModal.distance_meters}m del local</p>}
            </div>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/asistencia/foto/${photoModal.photo_filename}`}
              alt="Foto asistencia"
              className="w-full border border-zinc-200"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceAdminPage;
